import React, { useCallback, useEffect, useState } from "react";
import { AUTH_STORAGE_KEY } from "../socket";
import {
  loadAdminSliderImages,
  uploadSliderImage,
  updateSliderImage,
  deleteSliderImage,
} from "../api/sliderApi";

// ✅ Move outside component — defined before use
function getAuthHeaders() {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    const token = raw ? JSON.parse(raw)?.token : null;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch { return {}; }
}

function LoginSliderManagePage({ setToast }) {
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([]);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const loadImages = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const records = await loadAdminSliderImages(getAuthHeaders());
      setImages(Array.isArray(records) ? records : []);
    } catch (err) {
      const message = err.response?.data?.error || "Failed to load slider images";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadImages();
  }, [loadImages]);

  const handleFileSelect = async (files) => {
    if (!files || files.length === 0) return;
    const file = files[0];

    if (!file.type.startsWith("image/")) {
      setError("Please select a valid image file");
      return;
    }

    setUploading(true);
    setError("");
    try {
      const uploaded = await uploadSliderImage(file, getAuthHeaders());
      setImages((prev) => [...prev, uploaded]);
      setToast?.("Image uploaded successfully");
    } catch (err) {
      const message = err.response?.data?.error || "Failed to upload image";
      setError(message);
    } finally {
      setUploading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleToggleActive = async (id, isActive) => {
    try {
      await updateSliderImage(id, { isActive: !isActive }, getAuthHeaders());
      setImages((prev) =>
        prev.map((img) =>
          img.id === id ? { ...img, isActive: !isActive } : img
        )
      );
      setToast?.(
        !isActive ? "Image activated" : "Image deactivated"
      );
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update image");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this image?")) return;
    try {
      await deleteSliderImage(id, getAuthHeaders());
      setImages((prev) => prev.filter((img) => img.id !== id));
      setToast?.("Image deleted successfully");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete image");
    }
  };

  const handleReorder = async (id, newOrder) => {
    try {
      await updateSliderImage(id, { order: newOrder }, getAuthHeaders());
      setImages((prev) =>
        prev.map((img) =>
          img.id === id ? { ...img, order: newOrder } : img
        ).sort((a, b) => a.order - b.order)
      );
      setToast?.("Image order updated");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update order");
    }
  };

  return (
    <section className="admin-users" style={{ padding: "20px",overflowY: "auto", maxHeight: "calc(100vh - 100px)" }}>
      <header className="admin-users-header" style={{ marginBottom: "20px" }}>
        <h2 style={{ color: "black" }}>Login Page Slider Images</h2>
      </header>

      {error && (
        <div style={{ color: "#d32f2f", marginBottom: "10px", padding: "10px", color: "white", borderRadius: "4px" }}>
          {error}
        </div>
      )}

      {/* Upload Area */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        style={{
          border: "2px dashed " + (dragActive ? "#1976d2" : "#ccc"),
          borderRadius: "8px",
          padding: "40px",
          textAlign: "center",
          marginBottom: "30px",
          backgroundColor: dragActive ? "#e3f2fd" : "#fafafa",
          cursor: "pointer",
          transition: "all 0.2s",
        }}
      >
        <input
          type="file"
          id="file-input"
          accept="image/*"
          onChange={(e) => handleFileSelect(e.target.files)}
          disabled={uploading}
          style={{ display: "none" }}
        />
        <label htmlFor="file-input" style={{ cursor: "pointer", display: "block" }}>
          <p style={{ fontSize: "16px", fontWeight: "500", margin: 0,color: "#333" }}>
            {uploading ? "Uploading..." : "Drag images here or click to upload"}
          </p>
          <p style={{ fontSize: "12px", color: "#666", margin: "5px 0 0 0" }}>
            Supported formats: JPG, PNG, GIF, WebP, AVIF (Max 10MB)
          </p>
        </label>
      </div>

      {/* Images List */}
      {loading ? (
        <p>Loading images...</p>
      ) : images.length === 0 ? (
        <p style={{ color: "#999", fontStyle: "italic" }}>No images uploaded yet</p>
      ) : (
        <div style={{ display: "grid", gap: "16px" }}>
          {images.map((img) => (
            <div
              key={img.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                padding: "16px",
                border: "1px solid #eee",
                borderRadius: "8px",
                backgroundColor: img.isActive ? "#fff" : "#f5f5f5",
              }}
            >
              <img
                src={img.url}
                alt={img.originalName}
                style={{
                  width: "120px",
                  height: "80px",
                  objectFit: "cover",
                  borderRadius: "4px",
                  opacity: img.isActive ? 1 : 0.5,
                }}
              />
              <div style={{ flex: 1 }}>
                <h4 style={{ margin: "0 0 8px 0", fontSize: "14px" }}>
                  {img.originalName}
                </h4>
                <div style={{ fontSize: "12px", color: "#666", marginBottom: "8px" }}>
                  <p style={{ margin: "0 0 4px 0" }}>
                    Order: 
                    <input
                      type="number"
                      value={img.order}
                      onChange={(e) =>
                        handleReorder(img.id, parseInt(e.target.value) || 0)
                      }
                      style={{
                        width: "50px",
                        marginLeft: "4px",
                        padding: "4px",
                        borderRadius: "3px",
                        border: "1px solid #ccc",
                      }}
                    />
                  </p>
                  <p style={{ margin: "0" }}>
                    Status: {img.isActive ? "Active" : "Inactive"}
                  </p>
                </div>
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={() => handleToggleActive(img.id, img.isActive)}
                  style={{
                    padding: "8px 12px",
                    backgroundColor: img.isActive ? "#ff9800" : "#4caf50",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "12px",
                  }}
                >
                  {img.isActive ? "Deactivate" : "Activate"}
                </button>
                <button
                  onClick={() => handleDelete(img.id)}
                  style={{
                    padding: "8px 12px",
                    backgroundColor: "#f44336",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "12px",
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default LoginSliderManagePage;
